import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import {
  WithKemCard,
  WithoutKemCard,
  ProcessingKemCard,
  RejectedKemCard,
  ErrorOnFetchIssueState,
} from '@/features/kem-card'
import { useKemCardScreenState } from '@/domain/wallet/useCases'
import { useKemCardStore } from '@/store'
import { KemCardScreenState } from '@/store/useKemCardStore'

/**
 * Renders the appropriate screen component based on the card screen state
 */
const KemCardScreenContent: React.FC<{ screenState: KemCardScreenState }> = ({
  screenState,
}) => {
  switch (screenState.screenType) {
    case 'WITH_CARD':
      return (
        <WithKemCard
          kemCardId={screenState.kemCardId!}
          cardData={screenState.cardData}
          isLoading={screenState.isLoading}
        />
      )

    case 'WITHOUT_CARD':
      return <WithoutKemCard isLoadingKemCardState={screenState.isLoading} />

    case 'PROCESSING':
      return <ProcessingKemCard />

    case 'REJECTED':
      return <RejectedKemCard />

    case 'ERROR':
      return <ErrorOnFetchIssueState />

    case 'LOADING':
    default:
      return <WithoutKemCard isLoadingKemCardState={true} />
  }
}

/**
 * KemCardScreen wrapper component
 *
 * This component reads the pre-determined card screen state from the store
 * and renders the appropriate screen component.
 *
 * The state is persisted in localStorage and hydrated on app load, ensuring:
 * 1. Instant initial render from persisted state (no flash of wrong screen)
 * 2. Fresh state when user navigates to this screen via background refresh
 * 3. Consistent user experience across page reloads
 *
 * We check _hasHydrated from the KemCard store directly to ensure the persisted
 * screenState has been loaded before rendering. This prevents the flash of
 * WithoutKemCard when the user actually has a card.
 *
 * Polling behavior:
 * - Polls every 15 seconds while user is on this screen
 * - Only polls if user doesn't have a card yet
 * - Stops polling when user navigates away or gets a card
 */
export const KemCardScreen: React.FC = () => {
  const location = useLocation()

  // Trigger state determination on mount to ensure fresh state
  // This will refetch wallet and issue status if needed
  // Enable polling while user is on this screen (only if they don't have a card)
  const { refresh, userHasCard } = useKemCardScreenState({ enablePolling: true })

  // Trigger refresh when navigating to this screen (only if user doesn't have a card)
  useEffect(() => {
    if (location.pathname === '/kem-card' && !userHasCard) {
      refresh()
    }
  }, [location.pathname, refresh, userHasCard])

  // Read the determined state from the store
  // The store initializes with synchronously-read localStorage state,
  // so we get the correct screenType immediately without flash
  const screenState = useKemCardStore((state) => state.screenState)

  return <KemCardScreenContent screenState={screenState} />
}
